import React, { useState, useMemo } from 'react';
import {
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    isToday,
    format,
    isWeekend,
    isSameDay,
    parseISO,
    isWithinInterval,
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { motion } from 'framer-motion';
import {
    useDraggable,
    useDroppable,
} from '@dnd-kit/core';
import type { Technician, Assignment, ViewMode } from '@shared/types';
import { AssignmentModal } from '../AssignmentModal';
import { Tooltip } from '@shared/components/UI/Tooltip';
import { MapPin, Clock, CheckCircle2, Palmtree } from 'lucide-react';

interface MonthViewProps {
    currentDate: Date;
    technicians: Technician[];
    allTechnicians: Technician[];
    onUpdateTechnician: (techId: string, updates: Partial<Technician>) => void;
    showWeekends: boolean;
    onAssignmentClick?: (assignmentId: string) => void;
    readOnly?: boolean;
    onEditAssignment?: (assignment: Assignment) => void;
    onStartWorksite?: (assignmentId: string) => void;
    onFinishWorksite?: (assignmentId: string) => void;
    viewMode: ViewMode;
}

// --- Draggable/Droppable Components ---

const DraggableAssignment = ({ assignment, techId, date, children }: { assignment: Assignment, techId: string, date: Date, children: React.ReactNode }) => {
    // Unconditional hook usage
    const draggable = useDraggable({
        id: `assignment-${assignment.id}`,
        data: {
            type: 'assignment',
            assignment,
            techId,
            segmentDate: date.toISOString(),
        },
    });

    return (
        <div
            ref={draggable.setNodeRef}
            {...draggable.listeners}
            {...draggable.attributes}
            className={`cursor-grab active:cursor-grabbing ${draggable.isDragging ? 'opacity-30' : ''}`}
        >
            {children}
        </div>
    );
};

const DroppableCell = ({ techId, date, children, className, onClick }: { techId: string, date: Date, children: React.ReactNode, className?: string, onClick?: () => void }) => {
    const { setNodeRef, isOver } = useDroppable({
        id: `cell-${techId}-${format(date, 'yyyy-MM-dd')}`,
        data: {
            techId,
            date: date.toISOString(),
        },
    });

    return (
        <div
            ref={setNodeRef}
            onClick={onClick}
            className={`${className} ${isOver ? 'bg-primary/20 ring-inset ring-2 ring-primary' : ''} transition-colors cursor-pointer`}
        >
            {children}
        </div>
    );
};


// Helper for status colors with better palette
const getStatusColor = (status: string): string => {
    // Normalize status (handle casing, legacy values)
    const s = (status || 'todo').toLowerCase().trim();

    switch (s) {
        case 'in-progress':
        case 'in_progress':
            return 'bg-blue-600 border-blue-500'; // Blue for In Progress

        case 'done':
        case 'completed-by-tech':
        case 'completed':
            return 'bg-emerald-600 border-emerald-500'; // Green for success

        case 'pending': // Should not happen in calendar ideally
            return 'bg-yellow-600 border-yellow-500';

        case 'todo':
            return 'bg-orange-600 border-orange-500'; // Orange for Planned

        case 'closed':
            return 'bg-slate-700 border-slate-600 opacity-60'; // Gray/Dimmed for archived

        case 'blocked':
            return 'bg-red-600 border-red-500'; // Red for blocked

        default:
            return 'bg-orange-600 border-orange-500'; // Default to Orange (Planned)
    }
};

// --- Stats Calc ---
const getAnnualStats = (assignments: Assignment[]) => {
    const total = assignments.length;
    const duration = assignments.reduce((acc, curr) => acc + ((curr as any).duration || 0), 0);
    const hours = Math.round(duration / 60);
    return { total, hours };
};

export const MonthView: React.FC<MonthViewProps> = ({
    currentDate,
    technicians,
    allTechnicians,
    onUpdateTechnician,
    showWeekends,
    onAssignmentClick,
    readOnly,

    viewMode
}) => {
    // Debug Logging
    React.useEffect(() => {
        const totalAssignments = technicians.reduce((acc, t) => acc + t.assignments.length, 0);
        console.log('MonthView Debug:', {
            currentDate,
            techCount: technicians.length,
            totalAssignments,
            firstTech: technicians[0]
        });

        // Log invalid assignments to see why they are hidden
        const invalidAssignments = technicians.flatMap(t => t.assignments).filter(a => !a.startDate || !a.endDate);
        if (invalidAssignments.length > 0) {
            console.warn('Found invalid assignments (missing dates):', invalidAssignments);
        }
    }, [currentDate, technicians]);

    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedTechId, setSelectedTechId] = useState<string | null>(null);

    const days = useMemo(() => {
        let start, end;
        if (viewMode === 'week') {
            start = startOfWeek(currentDate, { weekStartsOn: 1 });
            end = endOfWeek(currentDate, { weekStartsOn: 1 });
        } else {
            start = startOfMonth(currentDate);
            end = endOfMonth(currentDate);
        }

        return eachDayOfInterval({ start, end }).filter(day => {
            if (!showWeekends) return !isWeekend(day);
            return true;
        });
    }, [currentDate, viewMode, showWeekends]);

    const handleDayClick = (techId: string, date: Date) => {
        if (readOnly) return;
        setSelectedTechId(techId);
        setSelectedDate(date);
    };

    const handleModalSave = (techId: string, updates: Partial<Technician>) => {
        onUpdateTechnician(techId, updates);
    };

    const selectedTech = selectedTechId ? technicians.find(t => t.id === selectedTechId) : null;

    const getAssignmentSegment = (assignment: Assignment, day: Date, isWknd: boolean) => {
        if (!assignment.startDate || !assignment.endDate) return null;

        const startDate = parseISO(assignment.startDate);
        const endDate = parseISO(assignment.endDate);

        if (!isWithinInterval(day, { start: startDate, end: endDate })) return null;
        if (isWknd && !assignment.includeWeekends) return null;

        const isStart = isSameDay(day, startDate);
        const isEnd = isSameDay(day, endDate);
        const continuesLeft = day > startDate;
        const continuesRight = day < endDate;

        return { isStart, isEnd, continuesLeft, continuesRight };
    };

    return (
        <div className="flex flex-col h-[calc(100vh-200px)] bg-transparent">

            {/* UNIFIED SCROLL CONTAINER */}
            <div
                id="month-view-scroll-container"
                className="flex-1 overflow-auto relative hide-scrollbar-force"
            >
                {/* HEADER ROW (Sticky Top) */}
                <div className="flex sticky top-0 z-[80] min-w-max bg-[var(--bg-color)] border-b border-glass-border">
                    {/* Top Left Corner (Empty/Title) - Sticky Left & Top */}
                    <div className="sticky left-0 top-0 z-[90] w-64 min-w-[16rem] p-4 flex items-center bg-[var(--bg-color)] shadow-sm border-r border-glass-border/30">
                        <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-accent">Planning</span>
                    </div>

                    {/* Dates Header - Sticky Top only */}
                    <div className="flex flex-1">
                        {days.map((day) => (
                            <div
                                key={`day-${day.toISOString()}`}
                                className={`
                                    flex-1 min-w-[3.5rem] h-12 flex flex-col items-center justify-center relative transition-all duration-300 border-r border-glass-border/10
                                    ${isToday(day) ? 'bg-primary/5' : ''}
                                `}
                            >
                                <span className={`text-lg font-bold leading-none tracking-tight ${isToday(day) ? 'text-primary scale-110' : 'text-muted/60'}`}>
                                    {format(day, 'dd')}
                                </span>
                                <span className="text-[8px] uppercase font-bold tracking-[0.2em] opacity-40 mt-1 text-main">
                                    {format(day, 'EEE', { locale: fr }).slice(0, 3)}
                                </span>
                                {/* Ligne verticale subtile pour guider l'oeil */}
                                <div className="absolute bottom-0 top-full w-px bg-glass-border/10 h-[200vh] pointer-events-none" />
                            </div>
                        ))}
                    </div>
                </div>


                {/* BODY ROWS */}
                <div className="min-w-max pt-2 space-y-2">
                    {technicians.map((tech, idx) => {
                        const stats = getAnnualStats(tech.assignments);

                        return (
                            <motion.div
                                key={tech.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.03, type: 'spring', stiffness: 120 }}
                                className="flex group relative rounded-xl hover:bg-white/[0.02] transition-colors"
                            >
                                {/* Sticky Name Card - Sticky Left */}
                                {/* SAME WIDTH AS HEADER LEFT COL (w-64) */}
                                <div className="sticky left-0 z-[60] w-64 min-w-[16rem]">
                                    {/* Inner card with spacing to create the 'floating' look without breaking alignment */}
                                    <div className="relative h-full pr-2 py-1 pl-2">
                                        <div className="h-full bg-[var(--bg-color)]/90 backdrop-blur-xl rounded-xl border border-white/10 p-2 flex items-center gap-3 shadow-lg group-hover:border-primary/30 transition-all">

                                            {/* Avatar + Tooltip */}
                                            <Tooltip
                                                content={
                                                    <div className="p-3 bg-slate-900/95 border border-glass-border rounded-xl shadow-2xl min-w-[180px] z-[100]">
                                                        <div className="flex items-center gap-3 mb-3 pb-2 border-b border-white/10">
                                                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-inner" style={{ backgroundColor: tech.color }}>
                                                                {tech.name.charAt(0).toUpperCase()}
                                                            </div>
                                                            <div>
                                                                <div className="font-bold text-white text-sm">{tech.name}</div>
                                                                <div className="text-[10px] text-gray-400 capitalize">{tech.service}</div>
                                                            </div>
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <div className="bg-white/5 rounded-lg p-2 flex flex-col items-center">
                                                                <span className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Interventions</span>
                                                                <div className="flex items-center gap-1 text-white font-bold">
                                                                    <CheckCircle2 size={12} className="text-emerald-400" />
                                                                    {stats.total}
                                                                </div>
                                                            </div>
                                                            <div className="bg-white/5 rounded-lg p-2 flex flex-col items-center">
                                                                <span className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Heures</span>
                                                                <div className="flex items-center gap-1 text-white font-bold">
                                                                    <Clock size={12} className="text-blue-400" />
                                                                    {stats.hours}h
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                }
                                            >
                                                <div
                                                    className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-sm relative overflow-hidden ring-2 ring-offset-1 ring-offset-[var(--bg-color)] ring-transparent group-hover:ring-primary/40 transition-all cursor-help"
                                                    style={{ backgroundColor: tech.color }}
                                                >
                                                    {tech.name.charAt(0).toUpperCase()}
                                                    <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent" />
                                                </div>
                                            </Tooltip>

                                            <div className="flex flex-col min-w-0">
                                                <div className="font-bold truncate text-sm text-main mb-0.5 group-hover:text-primary transition-colors">
                                                    {tech.name}
                                                </div>
                                                <div className="text-[9px] text-muted truncate border border-white/5 px-2 py-0.5 rounded-full inline-flex self-start bg-white/5">
                                                    {tech.service}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Track Lane - STRICT FLEX 1 alignment without negative margins */}
                                <div className="flex flex-1 relative bg-[var(--panel-bg)]/30 backdrop-blur-[2px] border-y border-white/5 my-1 rounded-r-xl">
                                    {days.map((day) => {
                                        const isWknd = isWeekend(day);
                                        const dateKey = format(day, 'yyyy-MM-dd');
                                        const override = tech.overrides?.[dateKey];
                                        const isVacation = override === 'vacation';



                                        const dayAssignments = tech.assignments.map(a => ({
                                            ...a,
                                            segment: getAssignmentSegment(a, day, isWknd)
                                        })).filter(a => a.segment);

                                        dayAssignments.sort((a, b) => a.id.localeCompare(b.id));

                                        return (
                                            <DroppableCell
                                                key={day.toISOString()}
                                                techId={tech.id}
                                                date={day}
                                                onClick={() => handleDayClick(tech.id, day)}
                                                className={`
                                                flex-1 w-0 min-w-[3.5rem] relative flex flex-col pt-2 px-0.5 gap-1 transition-colors duration-300 border-r border-white/5 overflow-hidden
                                                ${isWknd ? 'bg-black/[0.02]' : ''}
                                                ${isToday(day) ? 'bg-primary/[0.02]' : ''}
                                                hover:bg-white/[0.05]
                                            `}
                                            >
                                                {/* Vacation Pattern */}
                                                <div
                                                    className="absolute inset-0 z-0"
                                                    onClick={() => handleDayClick(tech.id, day)}
                                                >
                                                    {isVacation && (
                                                        <div className="w-full h-full flex items-center justify-center">
                                                            <Palmtree size={20} className="text-yellow-500/30" />
                                                        </div>
                                                    )}
                                                </div>

                                                {dayAssignments.map((assignment) => {
                                                    const statusClasses = getStatusColor(assignment.status || 'todo');

                                                    const assignmentContent = (
                                                        <div
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (onAssignmentClick) onAssignmentClick(assignment.id);
                                                            }}
                                                            className={`
                                                            h-7 text-[10px] flex items-center px-2 font-medium truncate backdrop-blur-md relative z-20 transition-all duration-300
                                                            cursor-pointer hover:shadow-lg hover:shadow-primary/20 hover:-translate-y-0.5 hover:brightness-110 active:scale-95
                                                            ${statusClasses}
                                                            ${assignment.segment?.isStart ? 'rounded-l-lg ml-0.5 shadow-sm' : 'ml-[-2px] rounded-l-none border-l-0 opacity-90'}
                                                            ${assignment.segment?.isEnd ? 'rounded-r-lg mr-0.5 shadow-sm' : 'mr-[-2px] rounded-r-none border-r-0 opacity-90'}
                                                        `}
                                                            style={{
                                                                color: '#fff',
                                                                backgroundImage: 'linear-gradient(to bottom, rgba(255,255,255,0.1), rgba(255,255,255,0))'
                                                            }}
                                                        >
                                                            {assignment.segment?.isStart && (
                                                                <span className="truncate w-full drop-shadow-sm">{assignment.clientName}</span>
                                                            )}
                                                        </div>
                                                    );

                                                    if (assignment.status === 'closed') {
                                                        return (
                                                            <div key={`${assignment.id}-${day.toISOString()}`} className="opacity-50 grayscale hover:grayscale-0 transition-all">
                                                                {assignmentContent}
                                                            </div>
                                                        );
                                                    }

                                                    return (
                                                        <DraggableAssignment
                                                            key={`${assignment.id}-${day.toISOString()}`}
                                                            assignment={assignment}
                                                            techId={tech.id}
                                                            date={day}
                                                        >
                                                            <Tooltip
                                                                content={
                                                                    <div className="space-y-1 min-w-[150px] relative z-50 p-2 glass-panel border-glass-border bg-slate-900/95 shadow-xl rounded-xl">
                                                                        <div className="font-bold text-xs flex items-center gap-2 text-white">
                                                                            <div className={`w-1.5 h-1.5 rounded-full ${(statusClasses || '').split(' ')[0]} shadow-[0_0_6px_currentColor]`} />
                                                                            {assignment.clientName}
                                                                        </div>
                                                                        <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
                                                                            <MapPin size={10} />
                                                                            {assignment.city}
                                                                        </div>
                                                                    </div>
                                                                }
                                                            >
                                                                {assignmentContent}
                                                            </Tooltip>
                                                        </DraggableAssignment>
                                                    );
                                                })}
                                            </DroppableCell>
                                        );
                                    })}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>

            {selectedTech && selectedDate && (
                <AssignmentModal
                    isOpen={!!selectedTech}
                    onClose={() => { setSelectedDate(null); setSelectedTechId(null); }}
                    technician={selectedTech}
                    availableTechnicians={allTechnicians}
                    date={selectedDate}
                    onSave={handleModalSave}

                />
            )}
        </div>
    );
};
